import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { createComponent } from '../core/create-component.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { AccordionContext } from './accordion.context.js';
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);
    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }
    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }
  return target;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpreadProps(target, source) {
  source = source != null ? source : {};
  if (Object.getOwnPropertyDescriptors) {
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
  } else {
    ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
const SkinEnum = {
  STANDARD: 'standard',
  PEEK: 'peek',
  COLOR_SWATCHES: 'color-swatches'
};
const propTypes = {
  skin: PropTypes.oneOf([SkinEnum.STANDARD, SkinEnum.PEEK, SkinEnum.COLOR_SWATCHES]),
  sizeVariant: deprecatedProp(PropTypes.oneOf(['standard', 'large']), DEPRECATED_PROP_MESSAGES.size),
  size: PropTypes.oneOf(['standard', 'large']),
  fullBleed: PropTypes.bool,
  single: PropTypes.bool,
  purpose: PropTypes.oneOf(['list']),
  expandedCollapsibles: PropTypes.objectOf(PropTypes.bool.isRequired),
  onRequestExpandedChange: PropTypes.func,
  defaultExpandedCollapsibles: PropTypes.objectOf(PropTypes.bool.isRequired)
};
const Accordion = createComponent({
  displayName: 'Accordion',
  root: 'div',
  propTypes,
  propConfig: {
    derivedProps(props) {
      const {
        single = false,
        expandedCollapsibles: controlledExpandedCollapsibleMap,
        onRequestExpandedChange: controlledOnRequestExpandedChange,
        // if no initial state is provided, start with all collapsible closed
        defaultExpandedCollapsibles: defaultExpandedCollapsibleMap
      } = props;
      const isControlled = controlledExpandedCollapsibleMap !== undefined;
      warning(
      // note that we check props.single rather than single; that's because we set a default value on single
      // so we use props.single so that we know if the true value was undefined before the default was applied
      !(isControlled && props.single !== undefined), 'Since `expandedCollapsibles` is provided, `single` will be ignored. The values in `expandedCollapsibles` will be treated as the source of truth');
      const [expandedCollapsibleMap, requestExpandedChange] = useControllableValue({
        value: controlledExpandedCollapsibleMap || undefined,
        onChange: controlledOnRequestExpandedChange || undefined,
        defaultValue: defaultExpandedCollapsibleMap || {},
        defaultOnChange: function (setUncontrolledExpandedCollapsibles) {
          for (var _len = arguments.length, onChangeArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
            onChangeArgs[_key - 1] = arguments[_key];
          }
          const [collapsibleId, expanded] = onChangeArgs;
          setUncontrolledExpandedCollapsibles(prevUncontrolledExpandedCollapsibles => _objectSpreadProps(_objectSpread({}, !single ? prevUncontrolledExpandedCollapsibles : {}), {
            [collapsibleId]: expanded
          }));
        }
      });
      return {
        expandedCollapsibleMap,
        requestExpandedChange
      };
    },
    processProps(props) {
      if (props.purpose === 'list') {
        return _objectSpreadProps(_objectSpread({}, props), {
          as: props.as || props.component || 'ol'
        });
      }
      return props;
    },
    classNames(props) {
      const {
        sizeVariant = 'standard',
        size = sizeVariant,
        skin,
        fullBleed
      } = props;
      const classNameSet = new Set(['swan-vanilla-ignore', 'swan-accordion']);
      if (size === 'large') classNameSet.add('swan-accordion-large');
      if (skin === 'peek' || skin === 'color-swatches') {
        classNameSet.add("swan-accordion-skin-".concat(skin));
      }
      if (fullBleed) classNameSet.add('swan-accordion-full-bleed');
      return classNameSet;
    },
    wrapRoot(props, derivedProps, element) {
      const {
        expandedCollapsibleMap,
        requestExpandedChange
      } = derivedProps;
      const {
        purpose
      } = props;
      return /*#__PURE__*/React.createElement(AccordionContext.Provider, {
        value: {
          expandedCollapsibleMap,
          requestExpandedChange,
          purpose
        }
      }, element);
    }
  }
});
export { Accordion, SkinEnum };